import DashboardGenComponent from "@/components/DashboardGenComponent.tsx";
import React, {useEffect, useState} from "react";
import CMS from "@/utils/cms";
import {LocalLanguage} from "@/types_and_interfaces/types";
import st from "@/styles/CreditLineDetails.module.scss"
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import DashboardClass from "@/utils/dashboard_class";
import DividerSimple from "@/ui/DividerSimple.tsx";
import {useNavigate, useParams} from 'react-router-dom';
import LoanInterface from "@/types_and_interfaces/loan_interface.ts";
import useLoans from "@/hooks/loans.ts";
import CommonClass from "@/utils/common.ts";
import {useAtom} from "jotai/index";
import {loan_sidebar_data, profile} from "@/atoms/profile.ts";
import PaymentHistory from "@/components/PaymentHistory.tsx";
import StaticRangeSlider from "@/ui/StaticRangeSlider.tsx";
import Pay from "@/components/Pay.tsx";
import PayToday from "@/ui/PayToday.tsx";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import useCommon from "@/hooks/common.ts";
import {CircularProgress} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import CreditLineRequestTransaction from "@/components/CreditLineRequestTransaction.tsx";
import CreditLineTransactionList from "@/components/CreditLineTransactionsList.tsx";
import { isMobile, isTablet } from 'react-device-detect';

const CreditLineDetails:React.FC = ({}) => {

    const {t, i18n} = useTranslation()
    const navigate = useNavigate()
    const common_hook = useCommon()
    const {urn} = useParams()
    const cr_line_d = CMS["credit-line"]
    const data = cr_line_d ?  CMS.langAwareFilter(cr_line_d, (i18n.language as LocalLanguage)) : {}
    const dc = DashboardClass
    const [, set_data] = useAtom(loan_sidebar_data)
    const [user] = useAtom(profile)
    const [money, setMoney] = useState<number>(5000)
    const loans = useLoans()
    const cr_line_loans = loans.getLoansByType("Credit Line")
    const credit_line: LoanInterface | undefined = loans.getLoanByContract(urn as string)
    const is_credit_line = typeof credit_line !== "undefined"
    const restant = is_credit_line && (credit_line?.loan_age || 0) > 0
    const loan_amn = is_credit_line ? Number(credit_line?.capital) - Number(credit_line?.avail_capital) : 0
    const settled = credit_line?.r_status === "settled" || credit_line?.status === "closed" || credit_line?.status === "finished"

    const apply = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const form = new FormData(e.currentTarget)

        const amount = form.get("amount")

        if(!user?.phone.length) {
            eventEmitter.emit(CommonClass.ModalEvents.SetPhone, true, "", t("set_phone_to_continue"))
            return;
        }

        if(amount && Number(amount)) {
            if(Number(amount) < 3000) {
                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "warning", t("min_mount_warn_cr_line"))
                return
            }
        }

        const data = {
            amount,
            // ...cr_line_data
        }

        set_data(JSON.stringify({
            amount: amount,
        }))

        if(Object.keys(data).length) {
            navigate(dc.dashboard_routes.ApplyCrLine + `/${urn}`)
        }
    }

    const header_type_handle  = () => {
        if(credit_line === undefined) return "gray"

        if(is_credit_line && typeof credit_line?.loan_age === "number" && credit_line.loan_age > 0) {
            return "danger"
        }

        if((is_credit_line && credit_line?.r_status === "settled") || settled) {
            return "gray"
        }

        switch (credit_line?.r_status_simplified) {
            case "unknown":
                return "gray"
            case "danger":
                return "danger"
            case "success":
                return "primary"
            case "pending":
                return "secondary"
            default:
                return "primary"
        }
    }

    useEffect(() => {
        if(Array.isArray(cr_line_loans) && !is_credit_line) {
            navigate(DashboardClass.dashboard_routes.CreditLine)
        }
    }, [cr_line_loans])

    return !!data && (
        <DashboardGenComponent
            header_type={header_type_handle()}

            responsive1

            backUrl={dc.dashboard_routes.CreditLine}

            heading={
                <div className={st.heading}>
                    <div className={st.title}>
                        {t("credit_line_details") + " "}
                        <p>
                            {t("nr") + " "}
                            <span onClick={() => common_hook.copyToClipboard(urn || "")} className={st.copy}>{urn}</span>
                        </p>
                    </div>
                    {["closed", "finished"]?.includes((credit_line as LoanInterface)?.status) && (
                       <div className={st.status}>
                            <div className={st.status_badge}>
                                {credit_line?.status === "finished" ? t('line_credit_status_finished')
                                    : credit_line?.status === "closed" ? t('line_credit_status_closed')
                                        : null}
                            </div>
                       </div>
                    )}
                </div>
            }

            header_content={is_credit_line ? (
                <div className={clsx(st.container, st.inf)}>
                    <div className={st.loan_sum}>
                        {t("cr_line_amount")}
                        <strong>
                            {CommonClass.formatMoneyAmount(settled ? Number(credit_line?.capital) : loan_amn)} {t("currency")}
                        </strong>
                    </div>

                    <StaticRangeSlider
                        value={loan_amn}
                        max={Number(credit_line?.capital)}
                        valuetext={"asd"}
                        danger={restant}
                        to_fixed
                    />

                    {!!credit_line?.inst_amount && (
                        <>
                            <DividerSimple/>

                            <div className={st.rows}>
                                {t("min_monthly_payment")}
                                <strong>
                                    {CommonClass.formatMoneyAmount(Number(credit_line?.inst_amount))} {" " + t("currency")}
                                </strong>
                            </div>
                        </>
                    )}

                    {(!!credit_line?.billing_deadline_days && !restant) && (
                        <>
                            <DividerSimple/>

                            <div className={st.rows}>
                                {t("next_payment")}

                                {credit_line?.billing_deadline_days === 0 ? (
                                    <PayToday />
                                ) : (
                                    <strong>
                                        {credit_line?.billing_deadline_days + " " + t(CommonClass.time_word_handling(credit_line?.billing_deadline_days))}
                                    </strong>
                                )}
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <Skeleton
                    variant="rectangular"
                    sx={{
                        width: "100%",
                        borderRadius:"16px",
                        minHeight: "250px",
                        bgcolor: "#969696"
                    }}
                />
            )}

            column_center={
                <div className={st.column_center}>
                    {is_credit_line ? (
                        <>
                            <Pay credit={(credit_line as LoanInterface)}  mobile/>

                            <CreditLineRequestTransaction
                                money={money}
                                moneyHandler={(n: number) => {
                                    setMoney(n)
                                }}
                                onSubmit={apply}
                                credit_line={credit_line as LoanInterface}
                                mobile={!(isMobile || isTablet)}
                            />

                            <CreditLineTransactionList
                                is_cr_line_page={false}
                                cr_line={credit_line as LoanInterface}
                            />

                            {/*@ts-ignore*/}
                            {(Array.isArray(credit_line?.payd_payments) && !!credit_line?.payd_payments.length) && (
                                <PaymentHistory
                                    payments={credit_line?.payd_payments}
                                />
                            )}
                        </>
                    ) : (
                        <CircularProgress
                            sx={{
                                display: "block",
                                margin: "0 auto",
                                color: "#000",
                            }}
                        />
                    )}
                </div>
            }

            column_right={is_credit_line && !settled && (
                <CreditLineRequestTransaction
                    money={money}
                    moneyHandler={(n: number) => {
                        setMoney(n)
                    }}
                    onSubmit={apply}
                    credit_line={credit_line as LoanInterface}
                    mobile={isMobile || isTablet}
                />
            )}
        />
    )
}

export default CreditLineDetails